package com.wintermar.inspection.core.ui.component.input.util

import androidx.compose.foundation.text.input.TextFieldState
import androidx.compose.foundation.text.input.placeCursorAtEnd
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.DrawableResource

class InputState(
    val initialData: InputStateModel,
    val scope: CoroutineScope,
    private val onBlurAction: (InputState) -> Unit = {},
) {
    val label = initialData.label
    val type = initialData.type
    val multiLine = initialData.multiLine
    val showAsterisk = initialData.validation.showAsterisk
    val maxChar = initialData.validation.maxChar
    val mandatory = initialData.validation.mandatory
    val leadingIcon = initialData.leadingIcon
    var trailingIcon by mutableStateOf(initialData.trailingIcon)
    val trailingIconClickable = initialData.trailingIconClickable
    val autoTrim = initialData.autoTrim
    var hasFocus by mutableStateOf(false)
    val placeholder = initialData.placeholder
    val value = TextFieldState(initialText = initialData.value)
    private var _oldValue by mutableStateOf(value.text)
    private var _showError by mutableStateOf(false)
    private val _validationResult by derivedStateOf {
        InputValidationErrorModel().validate(
            value = value.text.toString(),
            validation = initialData.validation,
            label = label,
            mandatoryMessage = initialData.validation.mandatoryMessage
        )
    }

    val isError get() = checkShowError()
    val message
        get() = if (checkShowError() && _validationResult.message.isNotBlank())
            _validationResult.message else initialData.informationMessage
    var enabled by mutableStateOf(!initialData.validation.disabled)

    init {
        scope.launch {
            snapshotFlow { value.text }.collect {
                _showError = false
            }
        }
    }

    private fun checkShowError(): Boolean {
        return _validationResult.hasError && _showError
    }

    fun validate(): Boolean {
        if (autoTrim) {
            val trimmedValue = value.text.toString().trim()
            this.value.edit {
                replace(0, length, trimmedValue)
                placeCursorAtEnd()
            }
        }
        val validResult = !_validationResult.hasError
        _showError = true
        return validResult
    }

    fun onBlurChanged() {
        var currentValue = value.text.toString()
        if (autoTrim) {
            currentValue = currentValue.trim()
            this.value.edit {
                replace(0, length, currentValue)
                placeCursorAtEnd()
            }
        }
        if (currentValue == _oldValue) {
            return
        }
        _oldValue = currentValue
        onBlurAction(this)
    }

    fun onSetValue(text: String) {
        val textToSet = if (autoTrim) text.trim() else text
        this.value.edit {
            replace(0, length, textToSet)
            placeCursorAtEnd()
        }
    }

    fun onClear(onAfterClear: () -> Unit = {}) {
        this.value.edit {
            replace(0, length, "")
            placeCursorAtEnd()
        }
        _oldValue = ""
        _showError = false
        onAfterClear()
    }

    fun onUpdateTrailingIcon(icon: DrawableResource?) {
        trailingIcon = icon
    }
}
